import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, User, LogOut, ShieldCheck } from 'lucide-react';

const API = '/api';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = React.useContext(AuthContext);
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [searchTurkishTitle, setSearchTurkishTitle] = useState('');
  const [searchPlaywright, setSearchPlaywright] = useState('');
  const [searchTranslator, setSearchTranslator] = useState('');
  const [searchGenre, setSearchGenre] = useState('');
  const [searchMale, setSearchMale] = useState('');
  const [searchFemale, setSearchFemale] = useState('');
  const [searchChild, setSearchChild] = useState('');
  
  // Metadata for dropdowns
  const [genres, setGenres] = useState([]);
  const [playwrights, setPlaywrights] = useState([]);
  const [translators, setTranslators] = useState([]);

  useEffect(() => {
    // Don't fetch plays on initial load - only after search
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      const response = await axios.get(`${API}/plays/metadata`);
      setGenres(response.data.genres);
      setPlaywrights(response.data.playwrights);
      setTranslators(response.data.translators);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const fetchPlays = async (filters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.title) params.append('title', filters.title);
      if (filters.turkish_title) params.append('turkish_title', filters.turkish_title);
      if (filters.playwright) params.append('playwright', filters.playwright);
      if (filters.translator) params.append('translator', filters.translator);
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.male_characters) params.append('male_characters', filters.male_characters);
      if (filters.female_characters) params.append('female_characters', filters.female_characters);
      if (filters.child_characters) params.append('child_characters', filters.child_characters);

      const response = await axios.get(`${API}/plays?${params.toString()}`);
      setPlays(response.data);
    } catch (error) {
      console.error('Error fetching plays:', error);
      toast.error('Oyunlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchPerformed(true);
    const filters = {};
    if (searchTitle) filters.title = searchTitle;
    if (searchTurkishTitle) filters.turkish_title = searchTurkishTitle;
    if (searchPlaywright) filters.playwright = searchPlaywright;
    if (searchTranslator) filters.translator = searchTranslator;
    if (searchGenre) filters.genre = searchGenre;
    if (searchMale) filters.male_characters = parseInt(searchMale);
    if (searchFemale) filters.female_characters = parseInt(searchFemale);
    if (searchChild) filters.child_characters = parseInt(searchChild);
    fetchPlays(filters);
  };

  const handleClearSearch = () => {
    setSearchTitle('');
    setSearchTurkishTitle('');
    setSearchPlaywright('');
    setSearchTranslator('');
    setSearchGenre('');
    setSearchMale('');
    setSearchFemale('');
    setSearchChild('');
    setSearchPerformed(false);
    setPlays([]);
  };

  const handlePlayClick = (playId) => {
    if (user.role === 'non-subscribed') {
      toast.error('Tam oyun detaylarını görmek için lütfen abone olun');
      return;
    }
    
    // Navigate to PDF viewer page
    navigate(`/pdf-viewer/${playId}`);
  };

  const handleLogout = () => {
    logout();
    toast.success('Başarıyla çıkış yapıldı');
    navigate('/');
  };

  return (
    <div className="curtain-bg min-h-screen">
      {/* Navbar */}
      <nav className="navbar fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src="/onk-logo.png" alt="ONK Ajans" style={{ width: '175px', height: 'auto' }} />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-900/20 rounded-lg border border-amber-700/30">
              <User className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-gray-300">{user?.email}</span>
              <span className="text-xs px-2 py-1 bg-amber-800/40 rounded text-amber-300">
                {user?.role === 'admin' ? 'yönetici' : user?.role === 'subscribed' ? 'abone' : 'abone değil'}
              </span>
            </div>
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-[#c50605] text-[#c50605] hover:bg-[#c50605]/10"
                data-testid="admin-panel-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Yönetici Paneli
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-400 hover:text-amber-400"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-60 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center mt-8">
            <h1 
              className="text-4xl sm:text-5xl font-bold gold-glow"
              data-testid="dashboard-title"
            >
              Oyun Metni Arama
            </h1>
          </div>

          {/* Search Section */}
          <div className="glass-effect rounded-xl p-6 mb-8">
            <form onSubmit={handleSearch} data-testid="search-form">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Oyunun Özgün Adı</Label>
                  <Input
                    placeholder="Özgün ada göre ara"
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                    className="bg-black/30 border-amber-800/40 text-white"
                    data-testid="search-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Oyunun Türkçe Adı</Label>
                  <Input
                    placeholder="Türkçe ada göre ara"
                    value={searchTurkishTitle}
                    onChange={(e) => setSearchTurkishTitle(e.target.value)}
                    className="bg-black/30 border-amber-800/40 text-white"
                    data-testid="search-turkish-title-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Oyun Yazarı</Label>
                  <Select
                    value={searchPlaywright}
                    onValueChange={setSearchPlaywright}
                    data-testid="search-playwright-select"
                  >
                    <SelectTrigger className="bg-black/30 border-amber-800/40 text-white">
                      <SelectValue placeholder="Yazar seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {playwrights.map((pw) => (
                        <SelectItem key={pw} value={pw}>{pw}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Çevirmen</Label>
                  <Select
                    value={searchTranslator}
                    onValueChange={setSearchTranslator}
                    data-testid="search-translator-select"
                  >
                    <SelectTrigger className="bg-black/30 border-amber-800/40 text-white">
                      <SelectValue placeholder="Çevirmen seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {translators.map((tr) => (
                        <SelectItem key={tr} value={tr}>{tr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Tür</Label>
                  <Select
                    value={searchGenre}
                    onValueChange={setSearchGenre}
                    data-testid="search-genre-select"
                  >
                    <SelectTrigger className="bg-black/30 border-amber-800/40 text-white">
                      <SelectValue placeholder="Tür seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {genres.map((genre) => (
                        <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Erkek Karakter Sayısı (en çok)</Label>
                  <Input
                    type="number"
                    placeholder="Erkek karakter sayısı"
                    value={searchMale}
                    onChange={(e) => setSearchMale(e.target.value)}
                    min="0"
                    className="bg-black/30 border-amber-800/40 text-white"
                    data-testid="search-male-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Kadın Karakter Sayısı (en çok)</Label>
                  <Input
                    type="number"
                    placeholder="Kadın karakter sayısı"
                    value={searchFemale}
                    onChange={(e) => setSearchFemale(e.target.value)}
                    min="0"
                    className="bg-black/30 border-amber-800/40 text-white"
                    data-testid="search-female-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Çocuk Karakter Sayısı (en çok)</Label>
                  <Input
                    type="number"
                    placeholder="Çocuk karakter sayısı"
                    value={searchChild}
                    onChange={(e) => setSearchChild(e.target.value)}
                    min="0"
                    className="bg-black/30 border-amber-800/40 text-white"
                    data-testid="search-child-input"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="bg-[#c50605] hover:bg-[#9d0504] text-white"
                  data-testid="search-submit-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Ara
                </Button>
                <Button
                  type="button"
                  onClick={handleClearSearch}
                  variant="outline"
                  className="border-[#c50605] text-[#c50605] hover:bg-[#c50605]/10"
                  data-testid="search-clear-btn"
                >
                  Temizle
                </Button>
              </div>
            </form>
          </div>

          {/* Results */}
          {searchPerformed && (
            <>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : plays.length === 0 ? (
                <div className="text-center py-12 glass-effect rounded-xl">
                  <p className="text-white text-lg" data-testid="no-plays-message">Oyun bulunamadı</p>
                </div>
              ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plays-grid">
              {plays.map((play) => (
                <div
                  key={play.id}
                  className="play-card p-6 rounded-xl cursor-pointer"
                  onClick={() => handlePlayClick(play.id)}
                  data-testid={`play-card-${play.id}`}
                >
                  <h3 
                    className="text-2xl font-bold mb-2 gold-glow"
                    data-testid={`play-title-${play.id}`}
                  >
                    {play.title}
                  </h3>
                  {play.turkish_title && (
                    <p className="text-gray-300 text-base mb-3">
                      {play.turkish_title}
                    </p>
                  )}
                  {user?.role !== 'non-subscribed' && (
                    <>
                      <div className="space-y-2 text-sm text-gray-300">
                        <p data-testid={`play-playwright-${play.id}`}>
                          <span className="text-amber-500 font-semibold">Oyun Yazarı:</span> {play.playwright}
                        </p>
                        {play.translator && play.translator.trim() && (
                          <p data-testid={`play-translator-${play.id}`}>
                            <span className="text-amber-500 font-semibold">Çevirmen:</span> {play.translator}
                          </p>
                        )}
                        <p data-testid={`play-genre-${play.id}`}>
                          <span className="text-amber-500 font-semibold">Tür:</span> {play.genre}
                        </p>
                        <p data-testid={`play-characters-${play.id}`}>
                          <span className="text-amber-500 font-semibold">Oyuncular:</span> {play.male_characters} erkek, {play.female_characters} kadın
                        </p>
                      </div>
                    </>
                  )}
                  {user?.role === 'non-subscribed' && (
                    <p className="text-gray-500 text-sm mt-2">Detayları görmek için abone olun</p>
                  )}
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}